'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

const React = require('react');
const prismicH = require('@prismicio/helpers');
const prismicR = require('@prismicio/richtext');

function _interopNamespace(e) {
	if (e && e.__esModule) return e;
	const n = Object.create(null);
	if (e) {
		for (const k in e) {
			if (k !== 'default') {
				const d = Object.getOwnPropertyDescriptor(e, k);
				Object.defineProperty(n, k, d.get ? d : {
					enumerable: true,
					get: function () { return e[k]; }
				});
			}
		}
	}
	n["default"] = e;
	return Object.freeze(n);
}

const React__namespace = /*#__PURE__*/_interopNamespace(React);
const prismicH__namespace = /*#__PURE__*/_interopNamespace(prismicH);
const prismicR__namespace = /*#__PURE__*/_interopNamespace(prismicR);

const PrismicContext = React__namespace.createContext({});
const PrismicProvider = ({
  client,
  linkResolver,
  richTextComponents,
  internalLinkComponent,
  externalLinkComponent,
  children
}) => {
  const value = React__namespace.useMemo(() => ({
    client,
    linkResolver,
    richTextComponents,
    internalLinkComponent,
    externalLinkComponent
  }), [
    client,
    linkResolver,
    richTextComponents,
    internalLinkComponent,
    externalLinkComponent
  ]);
  return /* @__PURE__ */ React__namespace.createElement(PrismicContext.Provider, {
    value
  }, children);
};

const usePrismicContext = () => {
  return React__namespace.useContext(PrismicContext) || {};
};

if (typeof process === "undefined") {
  globalThis.process = { env: {} };
}
const __PRODUCTION__ = process.env.NODE_ENV === "production";

const prefix = "Invariant failed";
function invariant(condition, message) {
  if (condition) {
    return;
  }
  if (__PRODUCTION__) {
    throw new Error(prefix);
  }
  throw new Error(`${prefix}: ${message || ""}`);
}

const usePrismicClient = (explicitClient) => {
  const context = usePrismicContext();
  const client = explicitClient || (context == null ? void 0 : context.client);
  invariant(client, "A @prismicio/client is required to query documents. Provide a client to the hook or to a <PrismicProvider> higher in your component tree.");
  return client;
};

var version = "2.4.2";

const devMsg = (slug) => {
  return `https://prismic.dev/msg/react/v${version}/${slug}`;
};

const isInternalURL = (url) => {
  const isInternal = /^(\/(?!\/)|#)/.test(url);
  const isSpecialLink = !isInternal && !/^https?:\/\//.test(url);
  return isInternal && !isSpecialLink;
};

const defaultInternalComponent = "a";
const defaultExternalComponent = "a";
const _PrismicLink = (props, ref) => {
  const context = usePrismicContext();
  if (!__PRODUCTION__) {
    if ("field" in props && props.field) {
      if (!props.field.link_type) {
        console.error(`[PrismicLink] This "field" prop value caused an error to be thrown.
`, props.field);
        throw new Error(`[PrismicLink] The provided field is missing required properties to properly render a link. The link will not render. For more details, see ${devMsg("missing-link-properties")}`);
      } else if (!(prismicH__namespace.isFilled.link(props.field) && ("url" in props.field || "id" in props.field))) {
        console.warn(`[PrismicLink] The provided field is missing required properties to properly render a link. The link may not render correctly. For more details, see ${devMsg("missing-link-properties")}`, props.field);
      }
    } else if ("document" in props && props.document) {
      if (!("url" in props.document || "id" in props.document)) {
        console.warn(`[PrismicLink] The provided document is missing required properties to properly render a link. The link may not render correctly. For more details, see ${devMsg("missing-link-properties")}`, props.document);
      }
    }
  }
  const linkResolver = props.linkResolver || context.linkResolver;
  let href;
  if ("href" in props) {
    href = props.href;
  } else if ("document" in props && props.document) {
    href = prismicH__namespace.asLink(props.document, linkResolver);
  } else if ("field" in props && props.field) {
    href = prismicH__namespace.asLink(props.field, linkResolver);
  }
  const isInternal = href && isInternalURL(href);
  const target = props.target || "field" in props && props.field && "target" in props.field && props.field.target || !isInternal && "_blank" || void 0;
  const rel = props.rel || (target === "_blank" ? "noopener noreferrer" : void 0);
  const InternalComponent = props.internalComponent || context.internalLinkComponent || defaultInternalComponent;
  const ExternalComponent = props.externalComponent || context.externalLinkComponent || defaultExternalComponent;
  const Component = isInternal ? InternalComponent : ExternalComponent;
  const passthroughProps = Object.assign({}, props);
  delete passthroughProps.linkResolver;
  delete passthroughProps.internalComponent;
  delete passthroughProps.externalComponent;
  delete passthroughProps.rel;
  delete passthroughProps.target;
  if ("field" in passthroughProps) {
    delete passthroughProps.field;
  } else if ("document" in passthroughProps) {
    delete passthroughProps.document;
  } else if ("href" in passthroughProps) {
    delete passthroughProps.href;
  }
  return href ? /* @__PURE__ */ React__namespace.createElement(Component, {
    ...passthroughProps,
    ref,
    href,
    target,
    rel
  }) : null;
};
if (!__PRODUCTION__) {
  _PrismicLink.displayName = "PrismicLink";
}
const PrismicLink = React__namespace.forwardRef(_PrismicLink);

const PrismicText = (props) => {
  if (!__PRODUCTION__) {
    if (typeof props.field === "string") {
      throw new Error(`[PrismicText] The "field" prop only accepts a Rich Text or Title field's value but was provided a different type of field instead (e.g. a Key Text or Select field). You can resolve this error by rendering the field value inline without <PrismicText>. For more details, see ${devMsg("prismictext-works-only-with-rich-text-and-title-fields")}`);
    }
  }
  return React__namespace.useMemo(() => {
    if (prismicH__namespace.isFilled.richText(props.field)) {
      const text = prismicH__namespace.asText(props.field, props.separator);
      return /* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, null, text);
    } else {
      return props.fallback != null ? /* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, null, props.fallback) : null;
    }
  }, [props.field, props.fallback, props.separator]);
};

const createDefaultSerializer = (args) => prismicR__namespace.wrapMapSerializer({
  heading1: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("h1", {
    key
  }, children),
  heading2: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("h2", {
    key
  }, children),
  heading3: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("h3", {
    key
  }, children),
  heading4: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("h4", {
    key
  }, children),
  heading5: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("h5", {
    key
  }, children),
  heading6: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("h6", {
    key
  }, children),
  paragraph: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("p", {
    key
  }, children),
  preformatted: ({ node, key }) => /* @__PURE__ */ React__namespace.createElement("pre", {
    key
  }, node.text),
  strong: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("strong", {
    key
  }, children),
  em: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("em", {
    key
  }, children),
  listItem: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("li", {
    key
  }, children),
  oListItem: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("li", {
    key
  }, children),
  list: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("ul", {
    key
  }, children),
  oList: ({ children, key }) => /* @__PURE__ */ React__namespace.createElement("ol", {
    key
  }, children),
  image: ({ node, key }) => {
    var _a;
    const img = /* @__PURE__ */ React__namespace.createElement("img", {
      src: node.url,
      alt: (_a = node.alt) != null ? _a : void 0,
      "data-copyright": node.copyright ? node.copyright : void 0
    });
    return /* @__PURE__ */ React__namespace.createElement("p", {
      key,
      className: "block-img"
    }, node.linkTo ? /* @__PURE__ */ React__namespace.createElement(PrismicLink, {
      linkResolver: args.linkResolver,
      internalComponent: args.internalLinkComponent,
      externalComponent: args.externalLinkComponent,
      field: node.linkTo
    }, img) : img);
  },
  embed: ({ node, key }) => {
    var _a;
    return /* @__PURE__ */ React__namespace.createElement("div", {
      key,
      "data-oembed": node.oembed.embed_url,
      "data-oembed-type": node.oembed.type,
      "data-oembed-provider": node.oembed.provider_name,
      dangerouslySetInnerHTML: { __html: (_a = node.oembed.html) != null ? _a : "" }
    });
  },
  hyperlink: ({ node, children, key }) => /* @__PURE__ */ React__namespace.createElement(PrismicLink, {
    key,
    field: node.data,
    linkResolver: args.linkResolver,
    internalComponent: args.internalLinkComponent,
    externalComponent: args.externalLinkComponent
  }, children),
  label: ({ node, children, key }) => /* @__PURE__ */ React__namespace.createElement("span", {
    key,
    className: node.data.label
  }, children),
  span: ({ text, key }) => {
    const result = [];
    let i = 0;
    for (const line of text.split("\n")) {
      if (i > 0) {
        result.push(/* @__PURE__ */ React__namespace.createElement("br", {
          key: `${i}__break`
        }));
      }
      result.push(/* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, {
        key: `${i}__line`
      }, line));
      i++;
    }
    return /* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, {
      key
    }, result);
  }
});
const PrismicRichText = (props) => {
  const context = usePrismicContext();
  return React__namespace.useMemo(() => {
    if (prismicH__namespace.isFilled.richText(props.field)) {
      const linkResolver = props.linkResolver || context.linkResolver;
      const serializer = prismicR__namespace.composeSerializers(typeof props.components === "object" ? prismicR__namespace.wrapMapSerializer(props.components) : props.components, typeof context.richTextComponents === "object" ? prismicR__namespace.wrapMapSerializer(context.richTextComponents) : context.richTextComponents, createDefaultSerializer({
        linkResolver,
        internalLinkComponent: props.internalLinkComponent,
        externalLinkComponent: props.externalLinkComponent
      }));
      const serialized = prismicR__namespace.serialize(props.field, (type, node, text, children, key) => {
        const result = serializer(type, node, text, children, key);
        if (React__namespace.isValidElement(result) && result.key == null) {
          return React__namespace.cloneElement(result, { key });
        } else {
          return result;
        }
      });
      return /* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, null, serialized);
    } else {
      return props.fallback != null ? /* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, null, props.fallback) : null;
    }
  }, [
    props.field,
    props.internalLinkComponent,
    props.externalLinkComponent,
    props.components,
    props.linkResolver,
    props.fallback,
    context.linkResolver,
    context.richTextComponents
  ]);
};

const _PrismicImage = (props, ref) => {
  const {
    field,
    alt,
    fallbackAlt,
    imgixParams,
    widths,
    pixelDensities,
    ...restProps
  } = props;
  if (!__PRODUCTION__) {
    if (typeof alt === "string" && props.alt !== "") {
      console.warn(`[PrismicImage] The "alt" prop can only be used to declare an image as decorative by passing an empty string (alt="") but was provided a non-empty string. You can resolve this warning by removing the "alt" prop or changing it to alt="". For more details, see ${devMsg("alt-must-be-an-empty-string")}`);
    }
    if (typeof fallbackAlt === "string" && fallbackAlt !== "") {
      console.warn(`[PrismicImage] The "fallbackAlt" prop can only be used to declare an image as decorative by passing an empty string (fallbackAlt="") but was provided a non-empty string. You can resolve this warning by removing the "fallbackAlt" prop or changing it to fallbackAlt="". For more details, see ${devMsg("alt-must-be-an-empty-string")}`);
    }
    if (widths && pixelDensities) {
      console.warn(`[PrismicImage] Only one of "widths" or "pixelDensities" props can be provided. You can resolve this warning by removing either the "widths" or "pixelDensities" prop. "widths" will be used in this case.`);
    }
  }
  if (prismicH__namespace.isFilled.imageThumbnail(field)) {
    let src;
    let srcSet;
    if (widths || !pixelDensities) {
      const res = prismicH__namespace.asImageWidthSrcSet(field, {
        ...imgixParams,
        widths: widths === "defaults" ? void 0 : widths
      });
      src = res.src;
      srcSet = res.srcset;
    } else if (pixelDensities) {
      const res = prismicH__namespace.asImagePixelDensitySrcSet(field, {
        ...imgixParams,
        pixelDensities: pixelDensities === "defaults" ? void 0 : pixelDensities
      });
      src = res.src;
      srcSet = res.srcset;
    }
    return /* @__PURE__ */ React__namespace.createElement("img", {
      ref,
      src,
      srcSet,
      alt: alt != null ? alt : field.alt || fallbackAlt,
      ...restProps
    });
  } else {
    return null;
  }
};
if (!__PRODUCTION__) {
  _PrismicImage.displayName = "PrismicImage";
}
const PrismicImage = React__namespace.forwardRef(_PrismicImage);

const pascalCase = (input) => {
  const camelCased = input.replace(/(?:-|_)(\w)/g, (_, c) => {
    return c ? c.toUpperCase() : "";
  });
  return camelCased[0].toUpperCase() + camelCased.slice(1);
};

const TODOSliceComponent = __PRODUCTION__ ? () => null : ({
  slice
}) => {
  const type = "slice_type" in slice ? slice.slice_type : slice.type;
  React__namespace.useEffect(() => {
    console.warn(`[SliceZone] Could not find a component for Slice type "${type}"`, slice);
  }, [slice, type]);
  return /* @__PURE__ */ React__namespace.createElement("section", {
    "data-slice-zone-todo-component": "",
    "data-slice-type": type
  }, "Could not find a component for Slice type \u201C", type, "\u201D");
};
const SliceZone = ({
  slices = [],
  components = {},
  resolver,
  defaultComponent = TODOSliceComponent,
  context = {}
}) => {
  const renderedSlices = React__namespace.useMemo(() => {
    return slices.map((slice, index) => {
      const type = "slice_type" in slice ? slice.slice_type : slice.type;
      let Comp = components[type] || defaultComponent;
      if (resolver) {
        const resolvedComp = resolver({
          slice,
          sliceName: pascalCase(type),
          i: index
        });
        if (resolvedComp) {
          Comp = resolvedComp;
        }
      }
      const key = `${index}-${JSON.stringify(slice)}`;
      return /* @__PURE__ */ React__namespace.createElement(Comp, {
        key,
        slice,
        index,
        slices,
        context
      });
    });
  }, [components, context, defaultComponent, slices, resolver]);
  return /* @__PURE__ */ React__namespace.createElement(React__namespace.Fragment, null, renderedSlices);
};

const PrismicToolbar = ({
  repositoryName,
  type = "new"
}) => {
  const src = `https://static.cdn.prismic.io/prismic.js?repo=${repositoryName}${type === "new" ? "&new=true" : ""}`;
  React__namespace.useEffect(() => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = src;
      script.defer = true;
      script.dataset.prismicToolbar = "";
      script.dataset.repositoryName = repositoryName;
      script.dataset.type = type;
      if (process.env.NODE_ENV === "test") {
        script._evaluateScript = false;
      }
      document.body.appendChild(script);
    }
  }, [repositoryName, type, src]);
  return null;
};

const reducer = (state, action) => {
  switch (action[0]) {
    case "start": {
      return { state: "loading" };
    }
    case "succeed": {
      return { state: "loaded", data: action[1] };
    }
    case "fail": {
      return {
        ...state,
        state: "failed",
        error: action[1]
      };
    }
  }
};
const initialState = {
  state: "idle"
};
const isParams = (value) => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
const useStatefulPrismicClientMethod = (methodName, args, explicitClient) => {
  const lastArg = args[args.length - 1];
  const {
    client: lastArgExplicitClient,
    skip,
    ...params
  } = isParams(lastArg) ? lastArg : {};
  const argsWithoutParams = isParams(lastArg) ? args.slice(0, -1) : args;
  const client = usePrismicClient(explicitClient || lastArgExplicitClient);
  const [state, dispatch] = React__namespace.useReducer(reducer, initialState);
  React__namespace.useEffect(() => {
    if (!skip) {
      {
        dispatch(["start"]);
      }
      client[methodName].call(client, ...argsWithoutParams, params).then((result) => {
        {
          dispatch(["succeed", result]);
        }
      }).catch((error) => {
        {
          dispatch(["fail", error]);
        }
      });
    }
  }, [
    client,
    methodName,
    skip,
    JSON.stringify(argsWithoutParams),
    JSON.stringify(params)
  ]);
  return React__namespace.useMemo(() => [
    state.data,
    {
      state: state.state,
      error: state.error
    }
  ], [state]);
};

const usePrismicPreviewResolver = (args = {}) => {
  const context = usePrismicContext();
  const linkResolver = args.linkResolver || context.linkResolver;
  const result = useStatefulPrismicClientMethod("resolvePreviewURL", [
    {
      linkResolver,
      defaultURL: args.defaultURL || "/",
      previewToken: args.previewToken,
      documentID: args.documentID
    }
  ], args.client);
  const [resolvedURL] = result;
  const { navigate } = args;
  React__namespace.useEffect(() => {
    if (resolvedURL && navigate) {
      navigate(resolvedURL);
    }
  }, [resolvedURL, navigate]);
  return result;
};

const usePrismicDocuments = (...args) => useStatefulPrismicClientMethod("get", args);
const useFirstPrismicDocument = (...args) => useStatefulPrismicClientMethod("getFirst", args);
const useAllPrismicDocumentsDangerously = (...args) => useStatefulPrismicClientMethod("dangerouslyGetAll", args);
const usePrismicDocumentByID = (...args) => useStatefulPrismicClientMethod("getByID", args);
const usePrismicDocumentsByIDs = (...args) => useStatefulPrismicClientMethod("getByIDs", args);
const useAllPrismicDocumentsByIDs = (...args) => useStatefulPrismicClientMethod("getAllByIDs", args);
const usePrismicDocumentByUID = (...args) => useStatefulPrismicClientMethod("getByUID", args);
const usePrismicDocumentsByUIDs = (...args) => useStatefulPrismicClientMethod("getByUIDs", args);
const useAllPrismicDocumentsByUIDs = (...args) => useStatefulPrismicClientMethod("getAllByUIDs", args);
const useSinglePrismicDocument = (...args) => useStatefulPrismicClientMethod("getSingle", args);
const usePrismicDocumentsByType = (...args) => useStatefulPrismicClientMethod("getByType", args);
const useAllPrismicDocumentsByType = (...args) => useStatefulPrismicClientMethod("getAllByType", args);
const usePrismicDocumentsByTag = (...args) => useStatefulPrismicClientMethod("getByTag", args);
const useAllPrismicDocumentsByTag = (...args) => useStatefulPrismicClientMethod("getAllByTag", args);
const usePrismicDocumentsBySomeTags = (...args) => useStatefulPrismicClientMethod("getBySomeTags", args);
const useAllPrismicDocumentsBySomeTags = (...args) => useStatefulPrismicClientMethod("getAllBySomeTags", args);
const usePrismicDocumentsByEveryTag = (...args) => useStatefulPrismicClientMethod("getByEveryTag", args);
const useAllPrismicDocumentsByEveryTag = (...args) => useStatefulPrismicClientMethod("getAllByEveryTag", args);

const Elements = prismicR.Element;

Object.defineProperty(exports, 'Element', {
	enumerable: true,
	get: function () { return prismicR.Element; }
});
exports.Elements = Elements;
exports.PrismicImage = PrismicImage;
exports.PrismicLink = PrismicLink;
exports.PrismicProvider = PrismicProvider;
exports.PrismicRichText = PrismicRichText;
exports.PrismicText = PrismicText;
exports.PrismicToolbar = PrismicToolbar;
exports.SliceZone = SliceZone;
exports.TODOSliceComponent = TODOSliceComponent;
exports.useAllPrismicDocumentsByEveryTag = useAllPrismicDocumentsByEveryTag;
exports.useAllPrismicDocumentsByIDs = useAllPrismicDocumentsByIDs;
exports.useAllPrismicDocumentsBySomeTags = useAllPrismicDocumentsBySomeTags;
exports.useAllPrismicDocumentsByTag = useAllPrismicDocumentsByTag;
exports.useAllPrismicDocumentsByType = useAllPrismicDocumentsByType;
exports.useAllPrismicDocumentsByUIDs = useAllPrismicDocumentsByUIDs;
exports.useAllPrismicDocumentsDangerously = useAllPrismicDocumentsDangerously;
exports.useFirstPrismicDocument = useFirstPrismicDocument;
exports.usePrismicClient = usePrismicClient;
exports.usePrismicContext = usePrismicContext;
exports.usePrismicDocumentByID = usePrismicDocumentByID;
exports.usePrismicDocumentByUID = usePrismicDocumentByUID;
exports.usePrismicDocuments = usePrismicDocuments;
exports.usePrismicDocumentsByEveryTag = usePrismicDocumentsByEveryTag;
exports.usePrismicDocumentsByIDs = usePrismicDocumentsByIDs;
exports.usePrismicDocumentsBySomeTags = usePrismicDocumentsBySomeTags;
exports.usePrismicDocumentsByTag = usePrismicDocumentsByTag;
exports.usePrismicDocumentsByType = usePrismicDocumentsByType;
exports.usePrismicDocumentsByUIDs = usePrismicDocumentsByUIDs;
exports.usePrismicPreviewResolver = usePrismicPreviewResolver;
exports.useSinglePrismicDocument = useSinglePrismicDocument;
//# sourceMappingURL=index.cjs.map
